import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import styled, { css } from 'styled-components';

const CheckboxContainer = styled.div`
  display: inline-block;
  vertical-align: middle;
`;

const Disabled = css`
  cursor: not-allowed;
  opacity: 0.3 !important;
`;

const Icon = styled.svg`
  fill: none;
  stroke: ${props =>
    props.theme.carvana[props.color] || props.theme.carvana.orange};
  stroke-width: 3px;
`;

// Hide checkbox visually but remain accessible to screen readers.
// Source: https://polished.js.org/docs/#hidevisually
const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const StyledCheckbox = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: ${props => (props.round ? 15 : 2)}px;
  cursor: pointer;
  transition: all 150ms;
  ${props =>
    props.checked
      ? css`
          border: 1px solid
            ${props.theme.carvana[props.color] || props.theme.carvana.orange};
        `
      : css`
          border: 1px solid ${props.theme.carvana.grayNineteen};
        `}

  ${Icon} {
    visibility: ${props => (props.checked ? 'visible' : 'hidden')};
  }

  ${props => props.disabled && Disabled}
`;

const CheckboxText = styled.span`
  font-weight: 700;
  color: ${props => props.theme.carvana.darkBlue4};
  margin-left: 20px;
  font-size: 1rem;
  text-align: left;
  cursor: pointer;

  ${props => props.disabled && Disabled}
`;

const CheckboxLabel = styled.label`
  align-items: center;
  cursor: pointer;
  display: flex;
`;

const Checkbox = props => {
  const { onTouched: ignoredOnTouched, round, validate, ...rest } = props;
  const { text, disabled, value, color, id, onChange } = rest;
  return (
    <Fragment>
      <CheckboxLabel htmlFor={id} data-cv-test={`${rest['data-cv-test']}Label`}>
        <CheckboxContainer data-cv-test={`${rest['data-cv-test']}Container`}>
          <HiddenCheckbox
            checked={value}
            {...rest}
            onChange={e => onChange(e, validate)}
          />
          <StyledCheckbox
            checked={value}
            round={round}
            disabled={disabled}
            color={color}
          >
            <Icon viewBox="0 0 24 24" color={color}>
              <polyline points="20 6 9 17 4 12" />
            </Icon>
          </StyledCheckbox>
        </CheckboxContainer>

        {text && <CheckboxText disabled={disabled}>{text}</CheckboxText>}
      </CheckboxLabel>
    </Fragment>
  );
};

Checkbox.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.node]),
  onTouched: PropTypes.func,
  disabled: PropTypes.bool,
  color: PropTypes.string,
  round: PropTypes.bool,
  uuid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  validate: PropTypes.func
};

Checkbox.defaultProps = {
  text: null,
  onTouched: null,
  disabled: false,
  color: null,
  round: false,
  validate: () => {}
};

export default Checkbox;
